import React, { useState } from 'react';
import { Col, Card, CardBody, CardHeader, Container, Row, Table, Input, Button } from 'reactstrap';
import { useLazyQuizLookupQuery, useLazyQuizDetailsQuery } from '../../features/ordersApiSlice';
import * as XLSX from 'xlsx';
import { formatQuizAnswer } from '../../utils/formatQuizAnswer';

const QuizLookup = () => {
  const [quizId, setQuizId] = useState('');
  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [triggerLookup, { data: lookupData, isLoading: lookupLoading, error: lookupError }] = useLazyQuizLookupQuery();
  const [triggerDetails, { data: detailsData, isLoading: detailsLoading, error: detailsError }] = useLazyQuizDetailsQuery();

  const handleLookup = () => {
    if (!quizId) return;
    setSelectedQuizId(null);
    triggerLookup(quizId);
  };

  const handleViewQuiz = async (customerQuizId) => {
    setSelectedQuizId(customerQuizId);
    setViewLoading(true);
    await triggerDetails(customerQuizId, false);
    setViewLoading(false);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const handleDownloadExcel = () => {
    if (!detailsData || !lookupData) return;

    const userName = (lookupData.user.name || 'Unknown').split(' ')[0];
    const filename = `${userName}-quizId-${quizId}.xlsx`;

    // Formula sheet
    const formulaRows = (detailsData.ingredients || []).map((item) => ({
      Ingredient: item.ingredient,
      'Daily Dose': parseFloat(item.daily_dose),
      Unit: 'mg',
      '% NRV': item.per_eu_nrv === 0 ? '*' : parseFloat(item.per_eu_nrv),
    }));
    const formulaSheet = XLSX.utils.json_to_sheet(formulaRows);

    // Quiz Answers sheet
    const qaRows = [];
    if (detailsData.personalInfo) {
      Object.entries(detailsData.personalInfo).forEach(([key, value]) => {
        qaRows.push({ Question: key, Answer: typeof value === 'object' ? JSON.stringify(value) : String(value) });
      });
    }
    (detailsData.questionsAnswers || []).forEach((qa) => {
      qaRows.push({ Question: qa.question, Answer: formatQuizAnswer(qa.answer) });
    });
    const qaSheet = XLSX.utils.json_to_sheet(qaRows);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, formulaSheet, 'Formula');
    XLSX.utils.book_append_sheet(wb, qaSheet, 'Quiz Answers');
    XLSX.writeFile(wb, filename);
  };

  return (
    <Container fluid>
      <Row>
        <Col sm="12">
          <Card>
            <CardHeader className="pb-0">
              <h5>QuizId Pull</h5>
              <span className="text-muted">Enter the quizId that you find in Omnisend (or in the URL sent to the customer) to look up user data, quiz answers, and formulas. This quizId corresponds to the id of the incomplete_quiz table in the DB.</span>
            </CardHeader>
            <CardBody>
              <div className="d-flex gap-2 mb-4" style={{ maxWidth: 400 }}>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter quizId..."
                  value={quizId}
                  onChange={(e) => setQuizId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                />
                <Button color="primary" onClick={handleLookup} disabled={lookupLoading || !quizId} style={{ whiteSpace: 'nowrap' }}>
                  {lookupLoading ? 'Loading...' : 'Look up'}
                </Button>
              </div>

              {lookupError && (
                <div className="alert alert-danger">
                  {lookupError?.data?.message || 'Error looking up quiz.'}
                </div>
              )}

              {lookupData && (
                <>
                  {/* User info */}
                  <Card className="mb-3 border">
                    <CardBody className="py-3">
                      <Row>
                        <Col md="4">
                          <small className="text-muted">User</small>
                          <p className="mb-0 fw-bold">{lookupData.user.name || '—'}</p>
                        </Col>
                        <Col md="4">
                          <small className="text-muted">Email</small>
                          <p className="mb-0">{lookupData.user.email}</p>
                        </Col>
                        <Col md="4">
                          <small className="text-muted">Quiz Progress</small>
                          <p className="mb-0">{lookupData.incompleteQuiz.progressBar}%</p>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* Customer quizzes table */}
                  <h6 className="mt-4 mb-2">Customer Quizzes ({lookupData.customerQuizzes.length})</h6>
                  <Table bordered striped responsive size="sm">
                    <thead>
                      <tr>
                        <th>Quiz ID</th>
                        <th>Created</th>
                        <th>Updated</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lookupData.customerQuizzes.map((q) => (
                        <tr key={q.id} className={selectedQuizId === q.id ? 'table-primary' : ''}>
                          <td>{q.id}</td>
                          <td>{formatDate(q.created_at)}</td>
                          <td>{formatDate(q.updated_at)}</td>
                          <td>
                            <Button
                              color={selectedQuizId === q.id ? 'primary' : 'outline-primary'}
                              size="sm"
                              onClick={() => handleViewQuiz(q.id)}
                              disabled={viewLoading}
                            >
                              {viewLoading && selectedQuizId === q.id ? 'Loading...' : 'View'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {lookupData.customerQuizzes.length === 0 && (
                        <tr><td colSpan="4" className="text-center text-muted">No completed quizzes found.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </>
              )}

              {viewLoading && (
                <div className="text-center my-4">
                  <div className="spinner-border text-primary" role="status" />
                  <p className="text-muted mt-2">Loading quiz details...</p>
                </div>
              )}

              {detailsError && (
                <div className="alert alert-danger mt-3">
                  {detailsError?.data?.message || 'Error loading quiz details.'}
                </div>
              )}

              {/* Quiz details */}
              {detailsData && selectedQuizId && (
                <>
                <div className="d-flex justify-content-end mb-2 mt-4">
                  <Button color="success" size="sm" onClick={handleDownloadExcel}>
                    Download Excel
                  </Button>
                </div>
                <Row>
                  {/* Formula */}
                  <Col md="6">
                    <Card className="border">
                      <CardHeader className="py-2">
                        <h6 className="mb-0">Formula (Quiz #{detailsData.customerQuizId || selectedQuizId})</h6>
                      </CardHeader>
                      <CardBody className="p-0">
                        <Table bordered size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>Ingredient</th>
                              <th>Daily Dose</th>
                              <th>% NRV</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailsData.ingredients?.map((item, i) => (
                              <tr key={i}>
                                <td>{item.ingredient}</td>
                                <td>{item.daily_dose} mg</td>
                                <td>{item.per_eu_nrv === 0 ? '*' : `${item.per_eu_nrv}%`}</td>
                              </tr>
                            ))}
                            {(!detailsData.ingredients || detailsData.ingredients.length === 0) && (
                              <tr><td colSpan="3" className="text-center text-muted">No ingredients.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Quiz Q&A */}
                  <Col md="6">
                    <Card className="border">
                      <CardHeader className="py-2">
                        <h6 className="mb-0">Quiz Answers</h6>
                      </CardHeader>
                      <CardBody className="p-0">
                        {/* Personal info */}
                        {detailsData.personalInfo && Object.keys(detailsData.personalInfo).length > 0 && (
                          <Table bordered size="sm" className="mb-0">
                            <thead>
                              <tr><th colSpan="2" className="bg-light">Personal Info</th></tr>
                            </thead>
                            <tbody>
                              {Object.entries(detailsData.personalInfo).map(([key, value]) => (
                                <tr key={key}>
                                  <td className="fw-bold text-capitalize" style={{ width: '35%' }}>{key}</td>
                                  <td>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}

                        {/* Questions & Answers */}
                        <Table bordered size="sm" className="mb-0">
                          <thead>
                            <tr><th colSpan="2" className="bg-light">Questions & Answers</th></tr>
                          </thead>
                          <tbody>
                            {detailsData.questionsAnswers?.map((qa, i) => (
                              <tr key={i}>
                                <td className="fw-bold" style={{ width: '35%' }}>{qa.question}</td>
                                <td>{formatQuizAnswer(qa.answer)}</td>
                              </tr>
                            ))}
                            {(!detailsData.questionsAnswers || detailsData.questionsAnswers.length === 0) && (
                              <tr><td colSpan="2" className="text-center text-muted">No quiz data.</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default QuizLookup;
